import type { ReactNode } from 'react'
import { Card, CardContent, CardHeader } from '@/components/Card'
import { Skeleton, useSkeletonListContext } from '@/components/Skeleton'
import styles from './OptionsCard.module.css'

export interface OptionsCardProps {
  title: string
  description?: string
  /**
   * Shown before the title, like the legend's icon.
   */
  imageUrl?: string
  /**
   * The card's fields, stacked.
   */
  children?: ReactNode
  testId?: string
}

/**
 * A group of related legend options.
 */
export function OptionsCard({
  title,
  description,
  imageUrl,
  children,
  testId,
}: OptionsCardProps) {
  return (
    <Card unconstrained testId={testId}>
      <CardHeader>
        <div className={styles.root}>
          {imageUrl && <img className={styles.image} src={imageUrl} alt="" />}
          <div className={styles.heading}>
            <h2 className={styles.title}>{title}</h2>
            {description && (
              <div className={styles.description}>{description}</div>
            )}
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className={styles.fields}>{children}</div>
      </CardContent>
    </Card>
  )
}

/**
 * A skeleton for an {@link OptionsCard}.
 */
export function OptionsCardSkeleton() {
  const { vary } = useSkeletonListContext()
  return (
    <Card unconstrained>
      <CardHeader>
        <div className={styles.heading}>
          <span className={styles.title}>
            <Skeleton width={vary(140)} />
          </span>
          <span className={styles.description}>
            <Skeleton width={vary(220)} />
          </span>
        </div>
      </CardHeader>
      <CardContent>
        <div className={styles.fields}>
          {[90, 120, 80].map((labelWidth) => (
            <div key={labelWidth} className={styles.fieldSkeleton}>
              <Skeleton width={vary(labelWidth)} />
              <Skeleton variant="rect" height="var(--control)" block />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  )
}
